import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Button, MessagePlugin, TableRowData, Tag } from 'tdesign-react';
import { v4 } from 'uuid';

import { IFormItemProps } from '@/components';
import { FormTitle } from '@/components/form/materials/Title';
import DialogForm from '@/components/form/wrapper/DialogForm';
import SimpleSortTable from '@/components/table/simpleSortTable';
import { HomeDataManagerType, HomeDataStyle } from '@/const/promotion';
import { eventPool } from '@/event/eventPool';
import { useEventBus } from '@/hooks/useEventBus';
import { ISpace, IStory } from '@/interface';
import { IPromotionItemInput } from '@/interface/promotion';
import { SpaceCard } from '@/pages/promotion/cards/spaceCard';
import { StoryCard } from '@/pages/promotion/cards/storyCard';
import { fetchPromotionItemDetail, modifyPromotionItemDetail } from '@/services/promotion';
import { OrgDataType, orgItemData } from '@/services/promotionData';
import { getMaterial } from '@/utils/material';

import { formKeys, ManageSelectOptions } from '../config';
import { formConfig, formStyle } from './form-config/common';
import styles from './index.module.less';

interface IProps {
  data: IPromotionItemInput[],
  onChange: (data: IPromotionItemInput) => void;
  onMove: (id: string) => void;
  onSort: (data: IPromotionItemInput[]) => void;
}
const MAX_TABLE_WIDTH = 1200;

const List: React.FC<IProps> = (props) => {
  const {
    data = [],
    onChange,
    onMove,
    onSort,
  } = props;
  const tableRef = useRef(null);
  const formRef = useRef<any>();
  const columns = [
    {
      colKey: formKeys.managerType,
      title: '类型',
      width: MAX_TABLE_WIDTH * 0.05,
      cell: ({ row }: { row: TableRowData }) => {
        const cname = ManageSelectOptions?.find(item => item.value === row?.[formKeys.managerType])?.label;
        return (
          <div><Tag theme="primary">{cname}</Tag></div>
        );
      },
    },
    {
      colKey: formKeys.title,
      title: '标题',
      width: MAX_TABLE_WIDTH * 0.1,
      cell: ({ row }: { row: TableRowData }) => {
        const name = row?.[formKeys.name];
        return (
          <div>{name || '--'} </div>
        );
      },
    },
    {
      colKey: 'cover',
      title: '合集类型',
      width: MAX_TABLE_WIDTH * 0.15,
      minWidth: 200,
      cell: ({ row }: { row: TableRowData }) => {
        if (row?.managerType === HomeDataManagerType.HOMEDATAMANAGERTYPE_COLLECTION
          || row?.managerType === HomeDataManagerType.HOMEDATAMANAGERTYPE_RANK) {
          return <SpaceCard key={row?.id} data={row?.space?.opt || row?.space} />;
        }
        return '--';
      },
    },
    {
      colKey: 'stories',
      title: '选择故事',
      width: MAX_TABLE_WIDTH * 0.35,
      cell: ({ row }: { row: TableRowData }) => {
        const items = row?.stories?.story || [];
        if (isEmpty(items)) {
          return '--';
        }
        return (
          <div className={styles.stories}>
            {items.map((item: Partial<IStory>) => (
              <StoryCard key={item.id} data={item} />
            ))}
          </div>
        );
      },
    },
    {
      colKey: 'op',
      title: '操作',
      width: MAX_TABLE_WIDTH * 0.1,
      align: 'center' as 'center',
      cell: ({ row }: { row: TableRowData }) => (
        <div className={styles.options}>
          <Button
            onClick={() => handleEdit(row)}
            theme="default"
            variant="text"
          >
            编辑
          </Button>
          <Button
            theme="default"
            variant="text"
            onClick={() => onMove(row.id)}
          >
            移除
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = async (row: TableRowData) => {
    let rowData = { ...row };
    if (row?.id) {
      const resData = await fetchDetail(row.id);
      rowData = orgItemData(OrgDataType.output, resData);
    }
    setFormData(rowData as IPromotionItemInput);
    setVisible(true);
  };

  const fetchDetail = async (id: string) => {
    const fetchItemDetail = fetchPromotionItemDetail;
    const { code, data, message } = await fetchItemDetail(id);
    if (code !== 0) {
      MessagePlugin.error({
        content: message || '获取失败，请重试',
      });
    }
    return data;
  };

  const getData = (data: IPromotionItemInput[]) => {
    onSort(data);
  };

  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState<IPromotionItemInput>({});
  const [materialMap, setMaterialMap] = useState<Record<string, string>>({});

  useEffect(() => {
    setMaterialMap(formData?.materialMap || {});
  }, [formData?.materialMap]);

  useEventBus(eventPool.eventsTypes.MATERIAL_CHANGE, (payload: any) => {
    const result = getMaterial(payload, { ...materialMap });
    setMaterialMap(result);
  });

  const onSubmit = async () => {
    const postData = { ...formData, materialMap };
    if (postData.managerType === HomeDataManagerType.HOMEDATAMANAGERTYPE_MULTI_COLLECTION) {
      if ((postData.multiCollection || []).length === 0) {
        MessagePlugin.error('至少需要一个榜单');
        return;
      }
      if ((postData.multiCollection || []).some(({ stories }) => (stories || []).length === 0)) {
        MessagePlugin.error('榜单故事不能为空');
        return;
      }
    }
    if (postData.managerType !== HomeDataManagerType.HOMEDATAMANAGERTYPE_MULTI_COLLECTION
      && postData.managerType !== HomeDataManagerType.HOMEDATAMANAGERTYPE_RANK
      && postData.managerType !== HomeDataManagerType.HOMEDATAMANAGERTYPE_OFFICIALGROUP
      && isEmpty(postData.stories)) {
      MessagePlugin.error('故事不能为空');
      return;
    }
    const modifyItemDetail = modifyPromotionItemDetail;
    const { code, data, message } = await modifyItemDetail({ ...postData });
    if (code !== 0) {
      MessagePlugin.error({
        content: message || '保存失败，请重试',
      });
      return false;
    }
    onChange({ ...postData, id: data.id });
  };

  const handleClose = () => {
    setFormData({});
    setVisible(false);
  };

  const onValuesChange = (content: IPromotionItemInput) => {
    const style = formStyle?.[
      content?.managerType
      || formData?.managerType
      || HomeDataStyle.HOMEDATASTYLE_DEFAULT
    ];
    let postData = { ...formData, ...content, style };
    const lastSpaceId = formData?.space?.id || (formData?.space as { opt?: ISpace })?.opt?.id;
    const newSpaceId = postData?.space?.id || (postData?.space as { opt?: ISpace })?.opt?.id;
    if (lastSpaceId !== newSpaceId) {
      postData.stories = {};
      postData.bots = {};
      formRef.current?.formRef?.form.setFieldsValue({ bots: {} });
      formRef.current?.formRef?.form.setFieldsValue({ stories: {} });
    }
    if (postData?.managerType !== formData?.managerType) {
      postData = { managerType: postData?.managerType, style };
      postData.stories = {};
      postData.bots = {};
      formRef.current?.formRef?.form.setFieldsValue({ bots: {} });
      formRef.current?.formRef?.form.setFieldsValue({ stories: {} });
    }
    setFormData(postData);
  };

  const orgConfigs = (
    configs: IFormItemProps[],
    formData: IPromotionItemInput,
    data: IPromotionItemInput[],
  ) => {
    const hasBanner = data?.some(e => e.managerType === HomeDataManagerType.HOMEDATAMANAGERTYPE_BANNER);
    return (
      configs?.map((e) => {
        if (e?.props === formKeys.stories || e?.props === formKeys.bots) {
          return {
            ...e,
            uiOptions: {
              ...e?.uiOptions,
              parentData: formData,
            },
          };
        }
        if (e?.props === formKeys.managerType) {
          const options = ManageSelectOptions;
          const selectOptions = options?.map((e) => {
            if (e.value === HomeDataManagerType.HOMEDATAMANAGERTYPE_BANNER && hasBanner) {
              return { ...e, disabled: true };
            }
            return e;
          });
          return {
            ...e,
            uiOptions: {
              ...e?.uiOptions,
              fetchOptions: async () => ({
                list: selectOptions,
              }),
            },
          };
        }
        return e;
      })
    );
  };

  const handleAddItem = () => {
    setVisible(true);
    const hasBanner = data?.some(e => e.managerType === HomeDataManagerType.HOMEDATAMANAGERTYPE_BANNER);
    if (hasBanner) {
      setFormData({ managerType: HomeDataManagerType.HOMEDATAMANAGERTYPE_RECOMMEND });
    } else {
      setFormData({ managerType: HomeDataManagerType.HOMEDATAMANAGERTYPE_BANNER });
    }
  };

  const addItem = () => (
    <div className={styles.addWrap}>
      <Button style={{ width: '100%', height: 40 }} variant="outline" theme="primary" ghost onClick={handleAddItem}>
        新增配置
      </Button>
    </div>
  );

  const refactorFieldsConfig = (
    config: IFormItemProps[],
    values: IPromotionItemInput,
    data: IPromotionItemInput[],
  ) => orgConfigs(config, values, data);

  const fieldsConfig = formConfig;

  return (
    <>
      <SimpleSortTable
        cRef={tableRef}
        columnsConfig={columns}
        dataList={data}
        dragWidth={100}
        getData={getData}
        extendsConfig={{}}
        tableConfig={{ bottomContent: <>{addItem()}</> }}
      />
      <DialogForm
        cRef={formRef}
        fieldsConfig={fieldsConfig[formData?.managerType
          || HomeDataManagerType.HOMEDATAMANAGERTYPE_BANNER]}
        onSubmit={onSubmit}
        refactorFieldsConfig={config => refactorFieldsConfig(config, formData, data)}
        onClose={handleClose}
        isVisible={visible}
        formData={{
          ...formData,
          space: {
            label: formData?.space?.name,
            value: formData?.space?.id,
            opt: formData?.space,
          },
          multiCollection: (formData?.multiCollection || []).map((item: any) => ({
            ...item,
            id: item.id || v4(),
          })),
        }}
        formConfig={{
          layout: 'vertical',
          labelWidth: 120,
          labelAlign: 'top',
          onValuesChange,
        }}
        layOutConfig={{
          paddingBottom: 30,
          maxHeight: '75vh',
        }}
        dialogConfig={{
          header: (
            <FormTitle
              title={'新增配置'}
              replaceObject={{}}
            />
          ),
          style: { width: '90%' },
          destroyOnClose: true,
        }}
      />
    </>
  );
};

export default List;
